/// <amd-module name="Build/Common/Library/Components/StagesPanel" />
import * as React from "react";

import {
    StageUpdateType,
    TaskResult,
    TimelineRecordState,
    UpdateStageParameters
} from "Build/Client/Contracts/Build";
import { FlatGraphRecord } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { NavConstants, TelemetryConstants } from "Build/Common/Library/Constants";
import { Status, getStatusIndicatorData, getTimelineStatusIndicatorData } from "Build/Common/Library/Indicator";
import { formatPipelineRunName } from "Build/Common/Library/PipelineRunNameFormatter";
import * as Resources from "Build/Common/Library/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Ago } from "VSSUI/Ago";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { Duration } from "VSSUI/Duration";
import { ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { CustomPanel, PanelContent, PanelHeader } from "VSSUI/Panel";
import { Status as StatusComponent, StatusSize } from "VSSUI/Status";
import { ColumnMore, IMeasurementStyle, ITableColumn, ITableRow, ITableRowDetails, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat, tooltipString } from "VSSUI/Utilities/Date";
import { FILTER_CHANGE_EVENT, Filter } from "VSSUI/Utilities/Filter";

interface IStageRow extends FlatGraphRecord {
    renderRow?: (index: number, item: IStageRow, details: ITableRowDetails<IStageRow>) => JSX.Element;
}

export interface IStagesPanelProps {
    stages: IObservableArray<IStageRow>;
    pageContext: IVssPageContext;
    buildId: number;
    buildNumber: string;
    pipelineName: string
    appendCommitMessageToRunName: boolean;
    sourceVersionMessage: string;
    canQueueBuild: boolean;
    onDismiss(): void;
}

export interface IStagesPanelState {
    foundResultCount: number;
    showError: boolean;
    errorMessage: string;
    messageSeverity: MessageCardSeverity;
}

export interface IStagesPanelTelemetryProperties {
    ProjectId?:  string; 
    BuildId?:  number; 
    DefinitionName?: string;
    States?:  string; 
    Source?:  string; 
    ActionName?: string; 
}

export class StagesPanel extends React.Component<IStagesPanelProps, IStagesPanelState> {
    private _columns: ITableColumn<IStageRow>[];
    private _itemProvider: IObservableArray<IStageRow | ObservableValue<IStageRow | undefined>>;
    private _stageFilter: Filter;
    private _projectId: string | undefined;
    private _sendTelemetryForSearch: boolean = true;
    private _currentStatusesSelected: string;
    private readonly _titleFilterKey = "titleFilter";
    private readonly _statusFilterKey = "statusFilter";
    private _queuedInfo = getStatusIndicatorData(Status.Queued);
    private _inProgressInfo = getStatusIndicatorData(Status.InProgress);
    private _succeededInfo = getStatusIndicatorData(Status.Succeeded);
    private _failedInfo = getStatusIndicatorData(Status.Failed);
    private _canceledInfo = getStatusIndicatorData(Status.Cancelled);
    private _skippedInfo = getStatusIndicatorData(Status.Skipped);
    private _waitingInfo = getStatusIndicatorData(Status.Waiting);
    private _warningInfo = getStatusIndicatorData(Status.Warning);
    
    private _stateFilterItems: IListBoxItem<string>[] = [
        {
            id: Status.Failed,
            text: Resources.StageStatusFailed,
            data: Status.Failed,
            iconProps: { render: () => <StatusComponent {...this._failedInfo.statusProps} size={StatusSize.m} animated={false} /> }
        }, 
        {
            id: Status.Waiting,
            text: Resources.StageStatusWaiting,
            data: Status.Waiting,
            iconProps: { render: () => <StatusComponent {...this._waitingInfo.statusProps} size={StatusSize.m} animated={false} /> }
        }, 
        {
            id: Status.Warning,
            text: Resources.StageStatusWarning,
            data: Status.Warning,
            iconProps: { render: () => <StatusComponent {...this._warningInfo.statusProps} size={StatusSize.m} animated={false} /> }
        },   
        {
            id: Status.InProgress,
            text: Resources.StageStatusRunning,
            data: Status.InProgress,
            iconProps: { render: () => <StatusComponent {...this._inProgressInfo.statusProps} size={StatusSize.m} animated={false} /> }
        },
        {
            id: Status.Succeeded,
            text: Resources.StageStatusSuccess,
            data: Status.Succeeded,
            iconProps: { render: () => <StatusComponent {...this._succeededInfo.statusProps} size={StatusSize.m} animated={false} /> }
        },        
        {
            id: Status.Queued,
            text: Resources.StageStatusQueued,
            data: Status.Queued,
            iconProps: { render: () => <StatusComponent {...this._queuedInfo.statusProps} size={StatusSize.m} animated={false} /> }
        }, 
        {
            id: Status.Skipped,
            text: Resources.StageStatusSkipped,
            data: Status.Skipped,
            iconProps: { render: () => <StatusComponent {...this._skippedInfo.statusProps} size={StatusSize.m} animated={false} /> }
        },
        {
            id: Status.Cancelled,
            text: Resources.StageStatusCanceled,
            data: Status.Cancelled,
            iconProps: { render: () => <StatusComponent {...this._canceledInfo.statusProps} size={StatusSize.m} animated={false} /> }
        }
    ];


    constructor(props: IStagesPanelProps) {
        super(props);
        const isActionsEnabled = isFeatureFlagEnabled(props.pageContext, "Build2.StagesPanel.EnableActions");
        this._itemProvider = new ObservableArray<IStageRow>([...props.stages.value]);
        this._initTableColumns(isActionsEnabled);
        this._stageFilter = new Filter();     
        const tfsPageData = getTFSPageData(this.props.pageContext); 
        this._projectId = tfsPageData?.project?.id;
        this.state = {
            foundResultCount: props.stages.length,
            showError: false,
            errorMessage: "",
            messageSeverity: MessageCardSeverity.Info,
        };
    }

    public componentDidMount(): void {
        this.props.stages.subscribe(this._onFilterChanged);
        this._stageFilter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
        this._publishTelemetryEvent(TelemetryConstants.StagePanelShow);
    }

    public componentDidUnMount(): void {
        this.props.stages.unsubscribe(this._onFilterChanged);
        this._stageFilter.unsubscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    public render() {
        const runName = formatPipelineRunName(this.props.buildNumber, this.props.sourceVersionMessage, this.props.appendCommitMessageToRunName);
        const isFilteringEnabled = isFeatureFlagEnabled(this.props.pageContext, "Build2.StagesPanel.EnableFiltering");

        return (
            <CustomPanel onDismiss={this.props.onDismiss} className="stages-panel">
                <PanelHeader
                    titleProps={{text: runName}}
                    description={this.props.pipelineName}
                    onDismiss={this.props.onDismiss}
                />
                <PanelContent className="panel-content-container">
                    {
                        isFilteringEnabled && 
                        <div className="stages-panel-filter-bar">
                            <KeywordFilterBarItem
                                className="stages-panel-title-filter"
                                placeholder={Resources.StagesFilterName} 
                                filterItemKey={this._titleFilterKey} 
                                filter={this._stageFilter} 
                            />  
                            <DropdownFilterBarItem
                                className="stages-panel-status-filter"
                                filterItemKey={this._statusFilterKey}
                                placeholder={Resources.StagesFilterStatus}
                                containerClassName="status-dropdown"
                                items={this._stateFilterItems}
                                filter={this._stageFilter} 
                                selection={new ListSelection({ multiSelect: true, alwaysMerge: true, selectOnFocus: false })}
                            />
                        </div>
                    }
                    {this.state.showError && (
                        <div className="margin-vertical-8 padding-horizontal-20">
                            <MessageCard severity={this.state.messageSeverity}>
                                <span className="multiline-error">{this.state.errorMessage}</span>
                            </MessageCard>
                        </div>
                    )}
                    {this._renderStagesTable(runName, isFilteringEnabled)}
                </PanelContent>
            </CustomPanel>
        );
    }    
    
    private _onFilterChanged = () => {
        const titleFilter = this._stageFilter.getFilterItemState(this._titleFilterKey); 
        const statusFilter = this._stageFilter.getFilterItemState(this._statusFilterKey);
        
        let results = [...this.props.stages.value];
        if (titleFilter || statusFilter) {
            results = this._performSearch(titleFilter?.value, statusFilter?.value);
        }
        
        this._itemProvider.splice(0, this._itemProvider.length, ...results);

        if (this.state.foundResultCount != results.length) {
            this.setState({
                foundResultCount: results.length
            });
        }
    };

    private _performSearch = (searchText: string, statuses: string): IStageRow[] => {
        let result = [...this.props.stages.value];
        
        if (searchText) {
            const lowerCaseSearchText = searchText?.toLowerCase();
            result = result.filter(item => {
                return item.name.toLowerCase().indexOf(lowerCaseSearchText) !== -1;
            });

            if(this._sendTelemetryForSearch){
                this._publishTelemetryEvent(TelemetryConstants.StagePanelSearch);
                this._sendTelemetryForSearch = false;
            }
        }
        
        if (statuses && statuses.length > 0) {
            result =  result.filter(item => {
                const statusData = getTimelineStatusIndicatorData(
                    item.state,
                    item.result,
                    item.stateData ? item.stateData.pendingChecks : undefined,
                    item.stateData ? !item.stateData.pendingDependencies : undefined
                );
                return statuses.includes(statusData.type); 
            });

            if(this._currentStatusesSelected != statuses){
                this._publishTelemetryEvent(TelemetryConstants.StagePanelFilter, {States: statuses});
                this._currentStatusesSelected = statuses;
            }
        } 
        
        if(!searchText && (!statuses || statuses.length == 0)) {
            this._stageFilter.reset();            
        }    
        
        return result;
    };

    private _publishTelemetryEvent = (feature: string, dataOverrides: IStagesPanelTelemetryProperties = {}) => {
        const telemetryService = this.props.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");      

        const baseData: IStagesPanelTelemetryProperties = {
              ProjectId: this._projectId,
              BuildId: this.props.buildId,
              DefinitionName: this.props.pipelineName
        }

        const telemetrydata = Object.assign(baseData, dataOverrides);

        telemetryService.publishEvent(
            TelemetryConstants.Area,
            feature,
            telemetrydata
        )

    }

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITableRow<IStageRow>, buildId: number, pageContext: IVssPageContext) => {
        if (!row.data) {
            return;
        }

        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const stageRoutes: { [key: string]: string } = {};
        stageRoutes[NavConstants.stage] = row.data.id;
        const stageUrl = linkingService.getBuildUrl(buildId, NavConstants.logs, undefined, undefined, stageRoutes);
        const telemetryProps = {
            ProjectId: this._projectId,
            BuildId: this.props.buildId,
            DefinitionName: this.props.pipelineName
        }

        onClickFPS(pageContext, stageUrl, true, event, { telemetrySource: TelemetryConstants.StagePanelLogs, telemetryProperties: telemetryProps });
    };
    
    private _renderDescriptionCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IStageRow>, tableItem: IStageRow, ariaRowIndex?: number): JSX.Element => {      
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <span className="stages-panel-status-icon">{this._renderBuildStatus(tableItem)}</span>
                    <span aria-label={tableItem.name}>{tableItem.name}</span>
            </SimpleTableCell>
        );
    };

    private _renderTimeStartedCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IStageRow>, tableItem: IStageRow, ariaRowIndex?: number): JSX.Element => {
        if(!tableItem.startTime){
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <div />
                </SimpleTableCell>
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-row flex-center fontSize">
                    { tableItem.startTime && <Ago 
                            date={tableItem.startTime} 
                            format={AgoFormat.Compact} 
                            tooltipProps={{ renderContent:  () => format(Resources.AgoTooltip, tooltipString(tableItem.startTime)) }} 
                        />
                    }
                </div>
            </SimpleTableCell>
        );
    };

    private _renderTimeElapsedCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IStageRow>, tableItem: IStageRow, ariaRowIndex?: number) : JSX.Element => {
        if(!tableItem.startTime || !tableItem.finishTime){
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <div />
                </SimpleTableCell>
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-row flex-center fontSize bolt-table-two-line-cell-item">
                    { (tableItem.startTime && tableItem.finishTime) && <Duration 
                            startDate={tableItem.startTime} 
                            endDate={tableItem.finishTime} 
                            tooltipProps={{ renderContent: () => format(Resources.DurationTooltip, tooltipString(tableItem.startTime)) }} 
                        />
                    }
                </div>
            </SimpleTableCell>
        );
    };

    private _renderBuildStatus = (stage: IStageRow): JSX.Element => {
        const statusData = getTimelineStatusIndicatorData(
            stage.state,
            stage.result,
            stage.stateData ? stage.stateData.pendingChecks : undefined,
            stage.stateData ? !stage.stateData.pendingDependencies : undefined
        );

        return (
            <Tooltip text={stage.name}>
                <span className="flex-row">
                    <StatusComponent size={StatusSize.m} {...statusData.statusProps} ariaLabel={statusData.label} />
                </span>
            </Tooltip>
        );        
    };

    private _initTableColumns = (isActionsEnabled: boolean) => {
        this._columns = [
            {
                id: "description",
                width: isActionsEnabled ? -40 : -50,
                ariaLabel: Resources.StagesTableDescription,
                name: Resources.StagesTableDescription,
                className: "stages-table-cell",
                renderCell: this._renderDescriptionCell,
                columnStyle: TableColumnStyle.Primary
            },
            {
                id: "started",
                width: -25,
                ariaLabel: Resources.StagesTableStarted,
                name: Resources.StagesTableStarted,
                className: "stages-table-cell",
                renderCell: this._renderTimeStartedCell
            },
            {
                ariaLabel: Resources.StagesTableElapsed,
                id: "elapsed",
                name: Resources.StagesTableElapsed,
                width: -25,
                className: "stages-table-cell",
                renderCell: this._renderTimeElapsedCell
            },
        ];
        
        if(isActionsEnabled) {
            const columnMore = new ColumnMore(this._getMenuForItem, this._showMenuForItem, undefined, 40);
            columnMore.widthStyle = IMeasurementStyle.Pixel;
            this._columns.push(columnMore);
        }
    };

    private _getMenuForItem = (stage: IStageRow) => {       
        const tfsPageData = getTFSPageData(this.props.pageContext); 
        const projectId = tfsPageData?.project?.id;
        const menuItems = [];

        if(this._canRetry(stage)) {
            const action = this._isFailed(stage) ? Resources.RerunFromFailed : Resources.RerunStage;
            menuItems.push({
                text: action,
                id: "re-run-stage",
                onActivate: () => {                    
                    this._onRetryActivate(stage, format(this._isFailed(stage) ? Resources.ConfirmRerunStageFailedJobs : Resources.ConfirmRerunStage, stage.name), false, action, projectId);               
                }
            })
             
            if (this._isFailed(stage)) {
                menuItems.push({
                    text: Resources.RerunAll,
                    id: "re-run-stage-all",
                    onActivate: () => {
                        this._onRetryActivate(stage, format(Resources.ConfirmRerunStage, stage.name), true, Resources.RerunAll, projectId);
                    }
                })
            }
        }
        
        const stageData = {
            buildId: this.props.buildId,
            buildNumber: this.props.buildNumber,
            pipelineName: this.props.pipelineName,
            sourceVersionMessage: this.props.sourceVersionMessage,
            projectId: projectId,
            stage: stage
        }

        const contributedItems = getMenuItems(this.props.pageContext, "ms.vss-build-web.stages-panel-context-menu", stageData);
        menuItems.push(...contributedItems.value);

        return {
            id: "stage-actions-menu",
            items: menuItems
        };
    };

    private _confirmAction = (title: string, description: string, callback: () => any) => {
        const layoutManager = this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss: () => void) => {
            const props = {
                title: title,
                message: description,
                onClose: onDismiss,
                onConfirm: callback
            };

            return React.createElement(CommandComponent, {
                commandType: "ciDConfirmPipelineComponent",
                ...props,
            }, null);
        });
    }

    private _canRetry = (stage: IStageRow) => {
        return stage.state === TimelineRecordState.Completed && this.props.canQueueBuild && (this._isSucceded(stage) || this._isFailed(stage));
    }

    private _isSucceded = (stage: IStageRow) => {
        return stage.result === TaskResult.Succeeded || stage.result === TaskResult.SucceededWithIssues;
    }

    private _isFailed = (stage: IStageRow) => {
        return stage.result === TaskResult.Failed || stage.result === TaskResult.Canceled;
    }

    private _showMenuForItem = (item: IStageRow) => {
        const contributedItems = getMenuItems(this.props.pageContext, "ms.vss-build-web.stages-panel-context-menu", item);
        return this._canRetry(item) || contributedItems.length > 0;
    };

    private _onRetryActivate = (stage: IStageRow, confirmationText: string, forceRetryAllJobs: boolean, actionName: string, projectId?: string) => {
        const _client = this.props.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        
        this._confirmAction(Resources.RerunStage, confirmationText, () => {
            const updateStageParameters = { state: StageUpdateType.Retry, forceRetryAllJobs: forceRetryAllJobs } as UpdateStageParameters;
            this._publishTelemetryEvent(TelemetryConstants.StagePanelAction, { ActionName: actionName });
            _client
                .updateStage(updateStageParameters, this.props.buildId, stage.name, projectId)
                .catch((reason: Error) => this.handleError(reason));
        });       
    };

    private _renderStagesTable = (runName: string, isFilteringEnabled: boolean): JSX.Element => {
        if (this.state.foundResultCount == 0 && isFilteringEnabled) {
            const text = Resources.NoResultsFound;
            Utils_Accessibility.announce(text);
            return (
                <div className="stages-panel-no-results">
                    {text}
                </div>
            );
        }

        return <Table
                    ariaLabel={runName}
                    className="stages-table sticky-table"
                    columns={this._columns}
                    itemProvider={this._itemProvider}
                    onActivate={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITableRow<IStageRow>) => this._onActivateRow(event, row, this.props.buildId, this.props.pageContext)}
                    showHeader={screenSize => screenSize >= ScreenSize.small}
                    spacerWidth={8}
                    showScroll
                    scrollable 
                />;
    }

    private handleError(reason: Error) {
        if (reason && reason.message) {
            this.setState({
                errorMessage: reason.message,
                showError: true,
                messageSeverity: MessageCardSeverity.Error
            });
        }
    }
}
